/**
 * The dialog layer, as a primitive.
 *
 * Rendered through a portal onto `document.body` rather than in place. A dialog opened
 * from inside a card would otherwise inherit that card's stacking context and
 * `overflow-hidden` - the timeline's own clipping is what would crop it - and no
 * z-index set from inside can escape an ancestor that clips.
 *
 * Three things it insists on, all of them the difference between a dialog and a div:
 * Escape closes it, a click on the backdrop closes it, and focus moves into the panel
 * on open so the keyboard is already where the reader is looking. Body scroll is locked
 * while it is open, because a dialog that scrolls the page behind it loses the reader's
 * place in the grid they will return to.
 */

import { useCallback, useEffect, useId, useRef } from 'react'
import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { X } from 'lucide-react'

import { cn } from '../../lib/cn'

interface ModalProps {
  open: boolean
  onClose: () => void
  title: ReactNode
  /** Secondary line under the title - a timestamp, a host, the context of the thing. */
  subtitle?: ReactNode
  children: ReactNode
  className?: string
}

export function Modal({ open, onClose, title, subtitle, children, className }: ModalProps) {
  const panelRef = useRef<HTMLDivElement | null>(null)
  const titleId = useId()

  // Escape is bound on the document, not the panel: it must work even when focus has
  // been dragged outside by a click on the backdrop.
  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  useEffect(() => {
    if (!open) return
    const previousOverflow = document.body.style.overflow
    // Whatever opened the dialog gets the focus back when it closes - otherwise a
    // keyboard reader who pressed Enter on a timeline cell is returned to the top of
    // the document and has to tab all the way back to where they were.
    const previouslyFocused = document.activeElement as HTMLElement | null
    document.body.style.overflow = 'hidden'
    panelRef.current?.focus()
    return () => {
      document.body.style.overflow = previousOverflow
      previouslyFocused?.focus?.()
    }
  }, [open])

  // Only a press that both started and ended on the backdrop closes: a text selection
  // dragged out of the panel and released on the backdrop is not a dismissal.
  const backdropDown = useRef(false)
  const onBackdropPointerDown = useCallback((event: React.PointerEvent) => {
    backdropDown.current = event.target === event.currentTarget
  }, [])
  const onBackdropClick = useCallback(
    (event: React.MouseEvent) => {
      if (backdropDown.current && event.target === event.currentTarget) onClose()
      backdropDown.current = false
    },
    [onClose],
  )

  if (!open) return null

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-black/40 p-0 backdrop-blur-[2px] sm:items-center sm:p-4"
      onPointerDown={onBackdropPointerDown}
      onClick={onBackdropClick}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          'card my-0 flex max-h-[90vh] w-full flex-col overflow-hidden rounded-b-none shadow-2xl outline-none',
          'sm:my-auto sm:max-w-xl sm:rounded-xl',
          className,
        )}
      >
        <div className="flex items-start justify-between gap-4 border-b px-5 py-3.5">
          <div className="min-w-0">
            <h2 id={titleId} className="text-sm font-semibold text-ink">
              {title}
            </h2>
            {subtitle ? (
              <p className="mt-0.5 text-xs text-ink-secondary">{subtitle}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mr-1 shrink-0 rounded-md p-1 text-ink-muted transition-colors hover:bg-surface-sunken hover:text-ink"
          >
            <X size={16} aria-hidden />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
      </div>
    </div>,
    document.body,
  )
}
